/**
 * Lecture d'une erreur renvoyée par `apiRequest` (« 403: {...} ») pour en
 * faire un message à l'écran. Les microservices Quarkus renvoient souvent un
 * corps vide sur 403 ou 400 : on s'appuie d'abord sur le code, puis sur un
 * éventuel message lisible du corps.
 *
 * Règle de l'espace paroisse : un refus du serveur s'affiche comme un refus,
 * jamais comme un succès.
 */
export function codeErreur(erreur: unknown): number | null {
  const message = (erreur as Error)?.message ?? "";
  const m = /^(\d{3}):/.exec(message);
  return m ? Number(m[1]) : null;
}

/**
 * Intitulés techniques (en anglais) que les services renvoient en `title` ou
 * `error` : ils ne disent rien à l'utilisateur, l'écran donne alors son
 * propre texte.
 */
const INTITULES_TECHNIQUES =
  /^(constraint violation|bad request|unauthorized|forbidden|not found|method not allowed|conflict|unprocessable entity|internal server error|bad gateway|service unavailable|gateway timeout)$/i;

/**
 * Message lisible porté par le corps de l'erreur, s'il y en a un. Jamais le
 * JSON brut : une validation refusée (Quarkus, `{"title":"Constraint
 * Violation","violations":[{"field","message"}]}`) donne le message de la
 * violation ; un corps sans phrase lisible donne null.
 */
export function messageDuCorps(erreur: unknown): string | null {
  const message = (erreur as Error)?.message ?? "";
  const corps = message.replace(/^\d{3}:\s*/, "").trim();
  if (!corps) return null;
  let json: unknown;
  try {
    json = JSON.parse(corps);
  } catch {
    // Corps texte : on ne le montre que s'il ressemble à une phrase.
    return corps.length < 240 && !corps.startsWith("<") ? corps : null;
  }
  if (!json || typeof json !== "object") return null;
  const objet = json as Record<string, unknown>;
  const violations = Array.isArray(objet.violations) ? objet.violations : [];
  const details = violations
    .map((v) => (v && typeof v === "object" ? (v as Record<string, unknown>).message : null))
    .filter((m): m is string => typeof m === "string" && m.trim() !== "")
    .map((m) => m.trim());
  if (details.length > 0) return Array.from(new Set(details)).join(" ; ");
  for (const cle of ["message", "error", "detail", "title"]) {
    const v = objet[cle];
    if (typeof v !== "string") continue;
    const texte = v.trim();
    if (texte && !/^\d{3}$/.test(texte) && !INTITULES_TECHNIQUES.test(texte)) return texte;
  }
  return null;
}

/**
 * Message à afficher : celui du serveur s'il est lisible, sinon celui prévu
 * pour le code (403, 400, 404, 409...), sinon le message par défaut.
 */
export function messageServeur(
  erreur: unknown,
  parCode: Partial<Record<number, string>>,
  parDefaut: string,
): string {
  const code = codeErreur(erreur);
  if (code !== null && parCode[code]) {
    const corps = messageDuCorps(erreur);
    // Pour un refus de droits, le texte prévu par l'écran est plus clair que
    // le libellé technique du serveur ; ailleurs, le serveur sait mieux.
    if (code === 403 || !corps) return parCode[code] as string;
    return corps;
  }
  return messageDuCorps(erreur) ?? parDefaut;
}

/** Texte d'un échec sans raison lisible du serveur. */
export const ECHEC_PAR_DEFAUT = "L'opération n'a pas abouti. Réessayez dans un instant.";
/** Texte d'un refus de droits (403). */
export const REFUS_PAR_DEFAUT = "Votre rôle ne permet pas cette action.";

/**
 * Message d'erreur à afficher dans un toast : la raison lisible du serveur,
 * le refus de droits dit en clair, sinon un texte simple. Remplace
 * `err instanceof Error ? err.message : …`, qui montrait le JSON brut de la
 * réponse (« 400: {"title":"Constraint Violation"… »).
 */
export function messageErreur(erreur: unknown, parDefaut: string = ECHEC_PAR_DEFAUT): string {
  return messageServeur(erreur, { 403: REFUS_PAR_DEFAUT }, parDefaut);
}

/**
 * Corps JSON d'une erreur renvoyée par `apiRequest` (« 409: {...} »), ou
 * null. Sert aux refus métier qui portent un code et des précisions : doublon
 * d'offrande (`DOUBLON_PROBABLE`, avec la première saisie), doublon de pièce
 * de versement (`DOUBLON_PIECE`), validation de la semaine refusée
 * (`VALIDATION_REFUSEE`, avec la liste des refus).
 */
export function corpsErreur(erreur: unknown): Record<string, unknown> | null {
  const message = (erreur as Error)?.message ?? "";
  const corps = message.replace(/^\d{3}:\s*/, "").trim();
  if (!corps.startsWith("{")) return null;
  try {
    const json = JSON.parse(corps);
    return json && typeof json === "object" && !Array.isArray(json) ? (json as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** Code métier du refus (`DOUBLON_PROBABLE`, `PIECE_REQUISE`…), ou null. */
export function codeMetier(erreur: unknown): string | null {
  const code = corpsErreur(erreur)?.code;
  return typeof code === "string" && code.trim() ? code.trim() : null;
}
